import { useState, useEffect, useCallback, useRef } from "react";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

const AUTO_INTERVAL_MS = 5000;

const STEPS = [
  {
    icon: <MenuBookOutlinedIcon />,
    title: "Выберите инструмент под задачу",
    desc: "Начните с базы знаний, если нужны подсказки на смене. Добавьте сценарий для обучения через решения. Подключите тренажёр, когда важно проверить и закрепить знания.",
    example: {
      label: "Три инструмента — одна платформа",
      items: [
        "База знаний — ответы на вопросы и ситуации",
        "Сценарий — интерактивное обучение с выбором",
        "Тренажёр — проверка знаний и рост компетенций",
      ],
    },
  },
  {
    icon: <EditNoteOutlinedIcon />,
    title: "Наполните контентом",
    desc: "Добавьте материалы в выбранный инструмент: пары «вопрос — ответ» для базы знаний, сцены и ветки для сценария, вопросы и правильные ответы для тренажёра.",
    example: {
      label: "Контент текстом или видео",
      items: [
        "Короткий текст — для быстрых подсказок",
        "Видео — когда важно показать наглядно",
        "Один формат на все три инструмента",
      ],
    },
  },
  {
    icon: <LinkOutlinedIcon />,
    title: "Опубликуйте и отправьте ссылку",
    desc: "Оформите подписку по размеру команды и поделитесь ссылкой с сотрудниками — они получат доступ к инструментам в Telegram или MAX.",
    example: {
      label: "Для команды",
      items: [
        "Одна ссылка на отдел или всю компанию",
        "Без персональных ключей и сложных настроек",
        "Подписка по числу сотрудников",
      ],
    },
  },
  {
    icon: <InsightsOutlinedIcon />,
    title: "Команда учится — вы видите результат",
    desc: "Сотрудники пользуются инструментами в мессенджере, а вы отслеживаете прогресс, сильные и слабые стороны команды и точки, где нужна дополнительная поддержка.",
    example: {
      label: "Результат для HR и руководителя",
      items: [
        "Меньше повторяющихся вопросов наставникам",
        "Понятно, кто усвоил материал, а где пробелы",
        "Один стандарт обучения для всей команды",
      ],
    },
  },
];

export default function CustomizedTimeline() {
  const [activeStep, setActiveStep] = useState(0);
  const pausedRef = useRef(false);

  const goToStep = useCallback((index) => {
    setActiveStep(index);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!pausedRef.current) {
        setActiveStep((prev) => (prev + 1) % STEPS.length);
      }
    }, AUTO_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  const step = STEPS[activeStep];

  return (
    <div
      className="steps-widget"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <div className="steps-widget__nav">
        {STEPS.map(({ title }, index) => (
          <button
            key={title}
            type="button"
            className={`steps-widget__step${
              index === activeStep ? " steps-widget__step--active" : ""
            }${index < activeStep ? " steps-widget__step--done" : ""}`}
            onClick={() => goToStep(index)}
            aria-current={index === activeStep ? "step" : undefined}
          >
            <span className="steps-widget__step-num">{index + 1}</span>
            <span className="steps-widget__step-label">{title}</span>
          </button>
        ))}
      </div>

      <div className="steps-widget__progress">
        <div
          className="steps-widget__progress-bar"
          style={{ width: `${((activeStep + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="steps-widget__panel" key={activeStep}>
        <div className="steps-widget__panel-icon">{step.icon}</div>
        <div className="steps-widget__panel-body">
          <h3 className="steps-widget__panel-title">{step.title}</h3>
          <p className="steps-widget__panel-desc">{step.desc}</p>
          <div className="steps-widget__example">
            <div className="steps-widget__example-label">{step.example.label}</div>
            <ul className="steps-widget__example-list">
              {step.example.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="steps-widget__dots">
        {STEPS.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`steps-widget__dot${
              index === activeStep ? " steps-widget__dot--active" : ""
            }`}
            onClick={() => goToStep(index)}
            aria-label={`Шаг ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
